import { useState } from "react";
import { X, FileText, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const INPUT_CLS =
  "bg-[#FFFFFF] border border-slate-200 rounded-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-10 px-3 w-full";

/**
 * Cover-page settings. Opens from the results toolbar so the transporter can
 * add a client-ready first page (client name, validity, notes) that prints
 * ahead of the loading plan.
 */
export default function CoverPageDialog({ show, cover, onSave, onClose }) {
  const [draft, setDraft] = useState(cover);

  if (!show) return null;

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/25 backdrop-blur flex items-center justify-center p-4"
      data-testid="cover-page-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl shadow-slate-900/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-[#FFFFFF]">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#2563EB]" />
            <div>
              <div className="overline">PDF first page</div>
              <div className="heading text-lg font-semibold">Client Cover Page</div>
            </div>
          </div>
          <button
            data-testid="close-cover-dialog"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-[#64748B] leading-relaxed">
            Adds a professional first page to your PDF export with the plan name,
            client name, quote validity, and a large signature block from your
            company — perfect for sharing quotes with clients.
          </p>

          <label
            data-testid="cover-enabled-toggle"
            className="flex items-center gap-3 p-3 border border-slate-200 hover:border-[#2563EB]/40 transition-colors cursor-pointer"
            onClick={() => setDraft((d) => ({ ...d, enabled: !d.enabled }))}
          >
            {draft.enabled ? (
              <CheckSquare className="w-5 h-5 text-[#2563EB]" />
            ) : (
              <Square className="w-5 h-5 text-slate-400" />
            )}
            <div>
              <div className="mono text-xs uppercase tracking-widest text-white/90">
                Include cover page on PDF export
              </div>
              <div className="mono text-[10px] text-[#64748B] mt-0.5">
                Only affects PDF downloads · Excel is unchanged
              </div>
            </div>
          </label>

          <label className="flex flex-col gap-1">
            <span className="overline">Client Name (Prepared For)</span>
            <Input
              data-testid="cover-client-name"
              className={INPUT_CLS}
              placeholder="e.g. Sankyu India Pvt Ltd"
              value={draft.client_name || ""}
              onChange={(e) => setDraft((d) => ({ ...d, client_name: e.target.value }))}
              maxLength={80}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="overline">Quote Valid Until</span>
            <Input
              data-testid="cover-validity"
              type="date"
              className={INPUT_CLS + " text-white/90"}
              value={draft.quote_validity || ""}
              onChange={(e) => setDraft((d) => ({ ...d, quote_validity: e.target.value }))}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="overline">Additional Notes (below signature)</span>
            <textarea
              data-testid="cover-notes"
              className={INPUT_CLS + " h-20 resize-none py-2"}
              placeholder="e.g. Rates exclude toll, detention beyond 24 hrs, and unloading charges."
              value={draft.notes || ""}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              maxLength={300}
              rows={3}
            />
          </label>

          <div className="mono text-[10px] text-slate-400 border border-slate-200 p-3 leading-relaxed">
            <span className="text-[#2563EB]">Tip:</span> The signature block uses
            your company name from Brand Settings. Set that first for a fully
            branded cover page.
          </div>
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 bg-[#FFFFFF] sticky bottom-0">
          <Button
            variant="ghost"
            data-testid="cover-cancel-btn"
            onClick={onClose}
            className="rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="cover-save-btn"
            onClick={handleSave}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
